import { Outlet, NavLink, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { API, api } from "@/lib/api";
import {
  LayoutDashboard, Calendar, Users, Scissors, Receipt, UserCog, Settings as SettingsIcon,
  LogOut, Package, TrendingDown, BarChart3, ShieldCheck, Command as CommandIcon,
  Zap, GitBranch, Mail, Megaphone, TrendingUp, Store, ChevronDown, Menu, X, Calculator,
  Facebook, Inbox as InboxIcon, Share2, Search, GraduationCap, Coins, LogIn,
  CircleCheck, Folder, Headset, PhoneCall, Clock, Bot,
} from "lucide-react";
import { CommandPalette } from "@/components/CommandPalette";
import { MementoLogo } from "@/components/Brand";
import { AGENCY_PERMS, hasAgencyPerm } from "@/lib/agencyPerms";

// Ogni item nav referenzia una "perm" (chiave sezione). Il salon_owner e
// l'agency_admin bypassano il filtro; lo staff vede solo le sezioni presenti
// in user.permissions. La Dashboard è sempre visibile (mostra dati già
// filtrati dalle sezioni a cui l'utente ha accesso).
function buildSalonNav() {
  return [
  { kind: "item", to: "/app", label: "nav.dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  {
    kind: "group", label: "nav.salon", icon: Store, tid: "grp-salone",
    children: [
      { to: "/app/agenda", label: "nav.agenda", icon: Calendar, tid: "nav-agenda", perm: "agenda" },
      { to: "/app/services", label: "nav.services", icon: Scissors, tid: "nav-services", perm: "services" },
      { to: "/app/inventory", label: "nav.inventory", icon: Package, tid: "nav-inventory", perm: "warehouse" },
    ],
  },
  {
    kind: "group", label: "nav.marketing", icon: Megaphone, tid: "grp-marketing",
    children: [
      { to: "/app/clients", label: "nav.clients", icon: Users, tid: "nav-clients", perm: "clients" },
      { to: "/app/inbox", label: "nav.inbox", icon: InboxIcon, tid: "nav-inbox", perm: "inbox" },
      { to: "/app/pipeline", label: "nav.pipeline", icon: GitBranch, tid: "nav-pipeline", perm: "pipeline" },
      { to: "/app/publish", label: "nav.publish", icon: Share2, tid: "nav-publish", perm: "publish" },
      { to: "/app/automations", label: "nav.automations", icon: Zap, tid: "nav-automations", perm: "automations" },
      { to: "/app/email", label: "nav.emailMarketing", icon: Mail, tid: "nav-email", perm: "email_marketing" },
    ],
  },
  {
    kind: "group", label: "nav.performance", icon: TrendingUp, tid: "grp-performance",
    children: [
      { to: "/app/invoices", label: "nav.invoices", icon: Receipt, tid: "nav-invoices", perm: "invoices" },
      { to: "/app/costs", label: "nav.costs", icon: TrendingDown, tid: "nav-costs", perm: "costs" },
      { to: "/app/staff-performance", label: "nav.staff", icon: UserCog, tid: "nav-staff-performance", perm: "staff_performance" },
      { to: "/app/ads-kpi", label: "nav.adsRoi", icon: Facebook, tid: "nav-ads-kpi", perm: "invoices" },
      { to: "/app/tax-calculator", label: "nav.taxCalculator", icon: Calculator, tid: "nav-tax", perm: "costs" },
    ],
  },
  { kind: "item", to: "/app/formazione", label: "nav.formazione", icon: GraduationCap, tid: "nav-formazione", perm: "formazione" },
  ];
}

// Restituisce true se l'utente può vedere questa voce di nav.
function canSee(user, perm) {
  if (!perm) return true;                              // items senza perm (Dashboard)
  if (!user) return false;
  const role = user.role;
  if (role === "agency_admin" || role === "salon_owner") return true;
  const permissions = user.permissions || [];
  return permissions.includes(perm);
}

// Filtra ricorsivamente la nav in base ai permessi dell'utente. Gruppi vuoti
// dopo il filtering non vengono renderizzati.
function filterNav(nav, user) {
  return nav
    .map((entry) => {
      if (entry.kind === "group") {
        const children = entry.children.filter((c) => canSee(user, c.perm));
        return children.length ? { ...entry, children } : null;
      }
      return canSee(user, entry.perm) ? entry : null;
    })
    .filter(Boolean);
}

const adminLinks = [
  { to: "/admin", label: "Panoramica", icon: LayoutDashboard, end: true, tid: "nav-agency" },
];

// Macro-sezioni del pannello admin, ognuna corrisponde a un reparto agenzia.
// Ordine di visualizzazione: Agency Workspace → Marketing → Coaching → Delivery → Amministrazione.
const adminGroups = [
  {
    tid: "grp-agency-workspace", label: "Receptionist", icon: Headset,
    children: [
      { to: "/admin/agency", end: true, label: "Panoramica", icon: LayoutDashboard, tid: "nav-aw-overview", agencyPerm: AGENCY_PERMS.overview },
      { to: "/admin/agency/pipeline", label: "Pipeline", icon: GitBranch, tid: "nav-aw-pipeline", agencyPerm: AGENCY_PERMS.pipeline },
      { to: "/admin/agency/lead-inbox", label: "Lead Inbox", icon: InboxIcon, tid: "nav-aw-lead-inbox", agencyPerm: AGENCY_PERMS.leadInbox },
      { to: "/admin/agency/calendario", label: "Calendario", icon: Calendar, tid: "nav-aw-calendario", agencyPerm: AGENCY_PERMS.calendar },
      { to: "/admin/agency/richiami", label: "Richiami", icon: PhoneCall, tid: "nav-aw-richiami", agencyPerm: AGENCY_PERMS.callbacks },
      { to: "/admin/agency/ai-bot", label: "AI Bot", icon: Bot, tid: "nav-aw-ai-bot", agencyPerm: AGENCY_PERMS.aiBot },
      { to: "/admin/agency/impostazioni", label: "Impostazioni", icon: SettingsIcon, tid: "nav-aw-impostazioni", agencyPerm: AGENCY_PERMS.settings },
    ],
  },
  {
    tid: "grp-agency-marketing", label: "Marketing", icon: Megaphone,
    children: [
      { to: "/admin/clients", label: "Contatti", icon: Users, tid: "nav-agency-contacts" },
      { to: "/admin/inbox", label: "Inbox", icon: InboxIcon, tid: "nav-agency-inbox" },
      { to: "/admin/publish", label: "Pubblica", icon: Share2, tid: "nav-agency-publish" },
      { to: "/admin/email", label: "Email Marketing", icon: Mail, tid: "nav-agency-email" },
      { to: "/admin/automations", label: "Automazioni", icon: Zap, tid: "nav-agency-automations" },
    ],
  },
  {
    tid: "grp-agency-coaching", label: "Coaching", icon: GraduationCap,
    children: [
      { to: "/admin/comparison", label: "Confronto Saloni", icon: BarChart3, tid: "nav-agency-comparison" },
      { to: "/admin/formazione", label: "Formazione", icon: GraduationCap, tid: "nav-agency-formazione" },
    ],
  },
  {
    tid: "grp-agency-delivery", label: "Delivery / Agenzia", icon: Store,
    children: [
      { to: "/admin/clienti", label: "Clienti", icon: Store, tid: "nav-agency-clienti" },
      { to: "/admin/ads-performance", label: "Tracking Ads", icon: TrendingUp, tid: "nav-agency-ads-performance" },
    ],   // onboarding saloni, ticket, board delivery
  },
  {
    tid: "grp-agency-admin", label: "Amministrazione", icon: Coins,
    children: [
      { to: "/admin/billing", label: "Fatturazione", icon: Coins, tid: "nav-billing" },
    ],
  },
];

function NavItem({ to, end, tid, icon: Icon, label, onClick }) {
  const { t } = useTranslation();
  return (
    <NavLink to={to} end={end} data-testid={tid} onClick={onClick}
      style={({ isActive }) => (isActive ? undefined : { color: "#FFFFFF" })}
      className={({ isActive }) => `flex items-center gap-3 px-6 py-2 text-sm font-medium ${isActive ? "active" : "hover:text-white"}`}
    >
      <Icon className="h-4 w-4" />
      {t(label)}
    </NavLink>
  );
}

// -----------------------------------------------------------------------------
// Bottom Nav Mobile — 5 icone rapide, appare solo su schermi < md
// -----------------------------------------------------------------------------
function BottomNavMobile() {
  const { t } = useTranslation();
  const items = [
    { to: "/app/agenda",     label: "nav.agenda",     icon: Calendar,       tid: "bnav-agenda" },
    { to: "/app/clients",    label: "nav.clients",   icon: Users,          tid: "bnav-clients" },
    { to: "/app/invoices",   label: "nav.invoices",    icon: Receipt,        tid: "bnav-invoices" },
    { to: "/app/formazione", label: "nav.formazione", icon: GraduationCap,  tid: "bnav-formazione" },
    { to: "/app",            label: "nav.dashboard",  icon: LayoutDashboard, tid: "bnav-dashboard", end: true },
  ];
  return (
    <nav className="sp-bottom-nav" data-testid="bottom-nav">
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end={i.end} data-testid={i.tid}
                 className={({ isActive }) => isActive ? "active" : ""}>
          <i.icon className="h-5 w-5" />
          <span>{t(i.label)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SubNavItem({ to, end, tid, icon: Icon, label, onClick, badge }) {
  const { t } = useTranslation();
  return (
    <NavLink to={to} end={end} data-testid={tid} onClick={onClick}
      className={({ isActive }) => `flex items-center gap-3 pl-12 pr-6 py-1.5 text-[13px] font-medium ${isActive ? "active" : ""}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="flex-1">{t(label)}</span>
      {badge > 0 && (
        <span data-testid={`${tid}-badge`}
          className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${tid === "nav-aw-richiami" ? "bg-rose-500 text-white" : "bg-[color:var(--sp-brand)] text-white"}`}>
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function NavGroup({ group, onNavigate, emptyLabel, badges = {} }) {
  const { t } = useTranslation();
  const location = useLocation();
  const hasActiveChild = group.children.some((c) => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(hasActiveChild);
  const Icon = group.icon;
  const isEmpty = !group.children || group.children.length === 0;
  return (
    <div>
      <button type="button" data-testid={group.tid} onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-3 px-6 py-2 text-sm font-medium transition-colors ${hasActiveChild ? "text-[color:var(--sp-brand)]" : "text-white hover:text-white"}`}
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{t(group.label)}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform opacity-60 ${open ? "" : "-rotate-90"}`} />
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-200 ${open ? "max-h-96" : "max-h-0"}`}>
        <div className="py-0.5">
          {isEmpty
            ? (
              <div className="pl-12 pr-6 py-1.5 text-[12px] italic text-white/40" data-testid={`${group.tid}-empty`}>
                {emptyLabel || "Nessuna voce"}
              </div>
            )
            : group.children.map((c) => <SubNavItem key={c.to} {...c} onClick={onNavigate} badge={badges[c.to]} />)}
        </div>
      </div>
    </div>
  );
}

function SidebarContent({ admin, onNavigate }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReceptionist = user?.role === "agency_receptionist";
  const salonNav = useMemo(
    () => (admin ? [] : filterNav(buildSalonNav(), user)),
    [admin, user],
  );
  const adminGroupsFiltered = useMemo(
    () => {
      if (!admin) return [];
      // Permessi AGENCY (dominio separato dai permessi tenant): la cartella
      // Receptionist mostra solo le sezioni consentite. L'admin vede tutto.
      const withAgencyPerms = (g) => ({
        ...g,
        children: g.children.filter((c) => !c.agencyPerm || hasAgencyPerm(user, c.agencyPerm)),
      });
      if (isReceptionist) {
        return adminGroups
          .filter((g) => g.tid === "grp-agency-workspace")
          .map(withAgencyPerms)
          .filter((g) => g.children.length > 0);
      }
      return adminGroups.map(withAgencyPerms);
    },
    [admin, isReceptionist, user],
  );
  // Fase 8 — contatori operativi per i badge sidebar (solo receptionist).
  const [counts, setCounts] = useState({ overdue: 0, unread: 0 });
  useEffect(() => {
    if (!isReceptionist) return;
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get("/agency/work-queue?counts_only=true");
        if (alive) setCounts({ overdue: r.data?.overdue || 0, unread: r.data?.unread || 0 });
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [isReceptionist]);
  const badges = useMemo(() => ({
    "/admin/agency/lead-inbox": counts.unread,
    "/admin/agency/richiami": counts.overdue,
  }), [counts]);
  return (
    <>
      <div className="px-6 py-5 border-b border-white/5 flex items-center justify-center">
        <MementoLogo size="lg" to={admin ? (isReceptionist ? "/admin/agency" : "/admin") : "/app"} variant="white" />
      </div>
      <nav className="flex-1 py-4 flex flex-col gap-0.5 overflow-y-auto">
        {admin ? (
          <>
            {!isReceptionist && adminLinks.map((l) => <NavItem key={l.to} {...l} onClick={onNavigate} />)}
            {adminGroupsFiltered.map((g) => (
              <NavGroup key={g.tid} group={g} onNavigate={onNavigate} emptyLabel="Arriverà presto" badges={badges} />
            ))}
          </>
        ) : (
          salonNav.map((entry) => (
            entry.kind === "group"
              ? <NavGroup key={entry.tid} group={entry} onNavigate={onNavigate} />
              : <NavItem key={entry.to} {...entry} onClick={onNavigate} />
          ))
        )}
      </nav>
      {!isReceptionist ? (
        <div className="p-4 border-t border-white/5">
          <NavLink
            to={admin ? "/admin/settings" : "/app/settings"}
            data-testid={admin ? "nav-agency-settings" : "nav-settings"}
            onClick={onNavigate}
            className={({ isActive }) => `w-full flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${isActive ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-white/10 text-white/70 hover:text-white hover:border-white/30"}`}
          >
            <SettingsIcon className="h-4 w-4" /> {t("nav.settings")}
          </NavLink>
        </div>
      ) : (
        <div className="p-4 border-t border-white/5">
          <NavLink
            to="/admin/agency/impostazioni"
            data-testid="nav-aw-impostazioni"
            onClick={onNavigate}
            className={({ isActive }) => `w-full flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${isActive ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-white/10 text-white/70 hover:text-white hover:border-white/30"}`}
          >
            <SettingsIcon className="h-4 w-4" /> {t("nav.settings")}
          </NavLink>
        </div>
      )}
    </>
  );
}

/** Header global search — clicking opens the ⌘K command palette. */
function HeaderSearch() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const openPalette = () => {
    window.dispatchEvent(new CustomEvent("open-command-palette"));
  };
  const label = isAdmin
    ? "Cerca task, progetti, contatti agenzia, saloni…"
    : t("nav.searchPlaceholder");
  return (
    <button
      type="button"
      data-testid="header-search-btn"
      onClick={openPalette}
      className="flex-1 max-w-xl flex items-center gap-3 px-4 h-10 rounded-full bg-[color:var(--sp-subtle)] border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]/40 hover:bg-white text-left transition-colors group"
    >
      <Search className="h-4 w-4 text-[color:var(--sp-text-muted)] group-hover:text-[color:var(--sp-brand)] flex-shrink-0" />
      <span className="text-sm text-[color:var(--sp-text-muted)] flex-1 truncate">{label}</span>
      <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono text-[color:var(--sp-text-muted)] bg-white border border-[color:var(--sp-border)] rounded px-1.5 py-0.5">
        <CommandIcon className="h-2.5 w-2.5" /> K
      </kbd>
    </button>
  );
}

/** User avatar + dropdown on the right of the header. */
function UserMenu({ user, onLogout, testidSuffix = "", admin = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  const tid = (name) => `header-${name}${testidSuffix}`;

  // Resolve avatar URL — supports both storage paths (/api/files/{path}?auth=...)
  // and full external URLs (Google picture, gravatar…).
  const token = typeof window !== "undefined" ? localStorage.getItem("sp_token") : "";
  const rawAvatar = user?.avatar_url || user?.picture || "";
  const avatarSrc = rawAvatar
    ? (rawAvatar.startsWith("http") ? rawAvatar : `${API}/files/${rawAvatar}${token ? `?auth=${token}` : ""}`)
    : null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={tid("user-btn")}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 pl-2 pr-3 h-10 rounded-full hover:bg-[color:var(--sp-subtle)] transition-colors"
      >
        <div className="h-8 w-8 rounded-full bg-[color:var(--sp-brand)]/10 border border-[color:var(--sp-border)] flex items-center justify-center overflow-hidden flex-shrink-0">
          {avatarSrc
            ? <img src={avatarSrc} alt={user?.name} className="h-full w-full object-cover" />
            : <span className="text-sm font-bold text-[color:var(--sp-brand)]">{initial}</span>}
        </div>
        <div className="hidden sm:block text-left leading-tight">
          <div className="text-sm font-bold text-[color:var(--sp-text)]">{user?.name || "Utente"}</div>
          <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate max-w-[160px]">{user?.email}</div>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)] hidden sm:block" />
      </button>

      {open && (
        <div
          data-testid={tid("user-menu")}
          className="absolute right-0 top-full mt-2 w-56 sp-card p-1 z-40 animate-in fade-in slide-in-from-top-1"
        >
          <div className="px-3 py-2 sm:hidden border-b border-[color:var(--sp-border)] mb-1">
            <div className="text-sm font-bold">{user?.name || "Utente"}</div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{user?.email}</div>
          </div>
          {!admin && (
            <Link
              to="/app/settings"
              data-testid={tid("user-settings")}
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[color:var(--sp-text)] hover:bg-[color:var(--sp-subtle)]"
            >
              <SettingsIcon className="h-4 w-4" /> {t("nav.settings")}
            </Link>
          )}
          <button
            type="button"
            data-testid={tid("user-logout")}
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[color:var(--sp-danger)] hover:bg-[color:var(--sp-danger)]/10"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      )}
    </div>
  );
}

export default function SalonLayout({ admin = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const impersonating = typeof window !== "undefined" && localStorage.getItem("sp_impersonating") === "1";

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const onLogout = () => { logout(); nav("/login"); };

  // Route guard: la receptionist vive SOLO nell'Agency Workspace, non nel resto
  // dell'admin. Qualsiasi altra rotta admin → redirect alla sua pipeline.
  if (admin && user?.role === "agency_receptionist" && !location.pathname.startsWith("/admin/agency")) {
    return <Navigate to="/admin/agency/pipeline" replace />;
  }

  const stopImpersonating = () => {
    const backup = localStorage.getItem("sp_token_admin_backup");
    if (backup) localStorage.setItem("sp_token", backup);
    else localStorage.removeItem("sp_token");
    localStorage.removeItem("sp_token_admin_backup");
    localStorage.removeItem("sp_impersonating");
    window.location.href = "/admin";
  };

  return (
    <div className="min-h-screen flex bg-[color:var(--sp-bg)]">
      {/* Impersonation banner */}
      {!admin && impersonating && (
        <div data-testid="impersonating-banner" className="fixed top-0 inset-x-0 z-50 bg-amber-500 text-white text-sm font-bold px-4 py-2 flex items-center justify-center gap-3">
          <ShieldCheck className="h-4 w-4" />
          <span>Stai accedendo come titolare (modalità supporto).</span>
          <button onClick={stopImpersonating} data-testid="stop-impersonating"
            className="ml-2 px-3 py-1 rounded-full bg-white text-amber-600 text-xs font-black hover:bg-amber-50">
            Torna al pannello admin
          </button>
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className="sp-sidebar w-64 hidden md:flex flex-col fixed inset-y-0 left-0 z-10">
        <SidebarContent admin={admin} />
      </aside>

      {/* Mobile top bar (drawer trigger) */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[color:var(--sp-border)] px-4 py-2 flex items-center gap-3 h-14">
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="h-9 w-9 rounded-lg flex items-center justify-center hover:bg-[color:var(--sp-subtle)]"
          aria-label="Apri menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <MementoLogo size="sm" to={admin ? "/admin" : "/app"} />
        <div className="ml-auto">
          <UserMenu user={user} onLogout={onLogout} testidSuffix="-mobile" admin={admin} />
        </div>
      </div>

      {/* Mobile bottom nav — 5 icone rapide (Agenda, Contatti, Incassi, Formazione, Dashboard) */}
      <BottomNavMobile />

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            data-testid="mobile-drawer-backdrop"
            onClick={() => setMobileOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-black/50 animate-in fade-in"
          />
          <aside
            data-testid="mobile-drawer"
            className="sp-sidebar md:hidden fixed inset-y-0 left-0 z-50 w-72 flex flex-col animate-in slide-in-from-left"
          >
            <div className="flex justify-end p-2">
              <button
                data-testid="mobile-close-btn"
                onClick={() => setMobileOpen(false)}
                className="h-9 w-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10"
                aria-label="Chiudi menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent
              admin={admin}
              onNavigate={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      <main className="flex-1 md:ml-64 h-screen overflow-hidden pt-14 md:pt-0 flex flex-col min-w-0">
        {/* Desktop top header */}
        <div className="hidden md:flex sticky top-0 z-20 h-16 bg-white/80 backdrop-blur-md border-b border-[color:var(--sp-border)] px-6 lg:px-10 items-center gap-4">
          <HeaderSearch />
          <div className="ml-auto">
            <UserMenu user={user} onLogout={onLogout} admin={admin} />
          </div>
        </div>
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto flex flex-col">
          <div className="max-w-7xl w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-10 py-4 sm:py-6 md:py-8 lg:py-10 flex-1 min-h-0 min-w-0 flex flex-col">
            <Outlet />
          </div>
        </div>
      </main>
      <CommandPalette />
    </div>
  );
}
